import { nodePathToFriendlyName } from '@ourbranch/lookups';
import { toCapitalized } from 'core/helpers/formatters';

/**
 * Humanizes the comma-joined node-path strings stored on `versionHistory[].itemsChanged`
 * and `.coverageChange` for display in the Version History modal.
 *
 * The backend (`policy-mod` segments.manager) stores raw flattened diff paths — e.g.
 * `additionalParties.0.address.VIN`, `renewalPaymentMethod`, `defaultCreditCard`. Rendering
 * those through the shared `toCapitalized` produced literal array indexes ("Additional Parties
 * 0 …") and split acronyms ("V I N"), with one verbose entry per changed subfield.
 *
 * This formatter, per path:
 *   1. strips array-index segments so `additionalParties.0.address.VIN` collapses toward its
 *      entity (the index isn't meaningful to an operator scanning the table),
 *   2. looks up a friendly label by full normalized path, then by top-level entity, falling back
 *      to an acronym-preserving capitalizer,
 *   3. de-dupes so multiple changed subfields of one entity render as a single entry.
 *
 * It re-derives display from the stored string on every render, so it fixes both historical
 * (already-mangled) and newly-written versions. See GH #29604 follow-up.
 */

// Entity-level labels for paths the shared lookup doesn't cover but that the modal commonly
// shows. Keyed by the index-stripped top-level (or normalized) node path.
const entityFriendlyName = {
  additionalParties: 'Additional party',
  renewalPaymentMethod: 'Renewal payment method',
  defaultCreditCard: 'Default credit card',
  defaultBankAccount: 'Default bank account',
  defaultEscrowAccount: 'Default escrow account'
};

// All-caps domain acronyms that `toCapitalized` would otherwise split into single letters
// ("V I N"). Matched case-insensitively as a whole final path segment.
const ACRONYMS = ['VIN', 'PIP', 'UM', 'UMPD', 'UIM', 'BIPD', 'ALE', 'FPC', 'DUI', 'MPD', 'SR22'];
const acronymBySegment = new Map(ACRONYMS.map((a) => [a.toLowerCase(), a]));

// Strip array-index segments (`.0.`, leading `0.`, trailing `.12`) from a dotted node path.
const stripArrayIndexes = (nodePath) =>
  nodePath
    .split('.')
    .filter((segment) => !/^\d+$/.test(segment))
    .join('.');

const labelForPath = (rawPath) => {
  const nodePath = rawPath.trim();
  if (!nodePath) {
    return '';
  }

  const normalizedPath = stripArrayIndexes(nodePath);
  const topLevelKey = normalizedPath.split('.')[0];

  // Prefer the most specific friendly label available, then collapse to entity level.
  const lookup =
    nodePathToFriendlyName[nodePath] ??
    nodePathToFriendlyName[normalizedPath] ??
    entityFriendlyName[normalizedPath] ??
    nodePathToFriendlyName[topLevelKey] ??
    entityFriendlyName[topLevelKey];
  if (lookup) {
    return lookup;
  }

  // No friendly label: collapse to the top-level entity. Nested changes (e.g. `cars.0.VIN`)
  // render as their entity ("Cars") rather than the leaf, matching the entity-level collapse
  // applied above. Only when the path IS a bare acronym (e.g. `VIN`) do we preserve it verbatim
  // so the shared `toCapitalized` doesn't split it into "V I N".
  if (acronymBySegment.has(topLevelKey.toLowerCase())) {
    return acronymBySegment.get(topLevelKey.toLowerCase());
  }
  return toCapitalized(topLevelKey);
};

/**
 * Humanize a stored `itemsChanged` / `coverageChange` string into a de-duped list of friendly
 * labels. Returns an empty array for empty/None input.
 * @param {string} changes - comma-joined node paths from versionHistory
 * @returns {string[]}
 */
export const versionChangeList = (changes) => {
  if (!changes || changes === 'None') {
    return [];
  }

  const labels = changes.split(',').map(labelForPath).filter(Boolean);

  return [...new Set(labels)];
};

/**
 * Format a stored `itemsChanged` / `coverageChange` string into a de-duped, human-readable
 * comma-separated list. Returns 'None' for empty/None input.
 * @param {string} changes - comma-joined node paths from versionHistory
 * @returns {string}
 */
export const formatVersionChanges = (changes) => versionChangeList(changes).join(', ') || 'None';
